import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "@/components/ui/modals/Modal";
import { BlendInfoForm } from "@/components/ui/forms/BlendInfoForm";
import { useSpices } from "@/hooks/useSpices";
import { useSetFilter } from "@/hooks/useFilter";
import { useExperienceStore } from "@/stores/useExperienceStore";
import { useUiStore } from "@/stores/useUiStore";
import { useLoadingStore } from "@/features/loading";
import { useShallow } from "zustand/shallow";
import { Loader2 } from "lucide-react";
import type { UiProps } from "@/ui";
import gsap from "gsap";
import { useGSAP } from '@gsap/react';
import { folder, useControls } from "leva";
import { EaseOptions } from "@/features/animations";

export function Shaking({ ani }: UiProps) {
  const { fill } = useSpices();
  const { setFilter } = useSetFilter();
  const { orbitControls, pre } = useExperienceStore(useShallow((s) => ({ orbitControls: s.orbitControls, pre: s.pre })));
  
  const onLoad = useLoadingStore((s) => s.onLoad);
  const setPhase = useUiStore((s) => s.setPhase);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (author: string, title: string) => {
    fill();
    setFilter({ title, author, isShaken: true });

    // setPhase Loading
    // onLoad ani.shake.resume().then(() => setPhase('postShake))

    setIsLoading(true);
    await onLoad();
    setIsLoading(false);
    ani.shake.resume().then();
    setPhase('postshake')
  }, [ani.shake, fill, setFilter, onLoad, setPhase]);

  const handleCancel = useCallback(() => {
    if (!orbitControls) return;
    ani.shake.progress(0);
    ani.shake.revert();
    Object.assign(orbitControls, pre.controls);
    orbitControls.update();
    setPhase('preshake');
  }, [ani.shake, orbitControls, pre, setPhase]);

  const modalRef = useRef<HTMLDivElement>(null);

  // animation on mount
    const { duration, ease } = useControls('Animation', {
      shake: folder({
        modal: folder({
          duration: { value: 4, min: 0, max: 5, step: 0.01 },
          ease: { value: EaseOptions.Power1In, options: Object.values(EaseOptions) },
        })
      })
  })
  
  // animation on mount
  useGSAP(() => {
    const delay = ani.shake.getLabelTime("afterAngles");
    gsap.fromTo(
      modalRef.current,
      { opacity: 0, scale: 0.95, pointerEvents: "none" },
      { opacity: 1, scale: 1, pointerEvents: "auto", duration: duration, ease: ease, delay }
    );

  }, {scope: modalRef, dependencies: [duration, ease]})

  return (
    <Modal ref={modalRef} className="pointer-events-none bg-transparent opacity-0">
      {isLoading && (
        <Loader2 className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 animate-spin text-tertiary z-100" />
      )}
      <BlendInfoForm handleSubmit={handleSubmit} handleCancel={handleCancel} />
    </Modal>
  );
}
